import { useEffect, useState } from 'react';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, DatePicker, Form, Input, Select } from 'antd';
import dayjs from 'dayjs';
import { cn } from '@/libs/utils';
import {
  EDUCATION_LEVEL_OPTIONS,
  RS_EDUCATION_LEVEL_OPTIONS,
} from '@/shared/employee';
import { DATE_FORMATS } from '@/utils/formUtils';
import { useEmployeeGeneralDataForm } from '../EmployeeGeneralDataFormContext';
import { Block, FieldGrid } from './profileBlocks';

const GENDERS = ['male', 'female', 'other'];
const MARITAL_STATUSES = ['single', 'married', 'divorced', 'widowed'];
const API = 'additionalPersonalInfo';

/**
 * The "Personal details" tile of the employee profile: identity, contact and
 * family in titled blocks. Same fields, rules and save as
 * EmployeePersonalFields and EmployeeAdditionalPersonalInfoForm, whose
 * personal fields step aside on the profile.
 */
export default function EmployeePersonalProfilePanel() {
  const ctx = useEmployeeGeneralDataForm();
  const { companyData, form, readOnly, sectionClass, t } = ctx;
  const f = (key) => t(key, { ns: 'forms' });
  const fv = (key) => t(`formValidation.${key}`, { ns: 'forms' });
  const tp = (key) =>
    t(`employeeProfile.personalUi.${key}`, { ns: 'translations' });
  const state = (name) => ctx[`${name}State`];
  const keys = (name) => ctx[`${name}Keys`];
  const market = companyData?.market || 'RS';

  // A generalData field: shown when the server lets this user see it.
  const gdField = (name, label, rules, input) =>
    state(name)?.visible ? (
      <Form.Item key={name} name={keys(name)} label={label} rules={rules}>
        {input(state(name)?.disabled)}
      </Form.Item>
    ) : null;
  const gdText = (name, label, rules) =>
    gdField(name, label, rules, (disabled) => (
      <Input placeholder={label} disabled={disabled} />
    ));

  const datePicker = (placeholder, props = {}) => (
    <DatePicker
      className="w-full"
      format={DATE_FORMATS.DISPLAY}
      placeholder={placeholder}
      {...props}
    />
  );

  const educationLevel = Form.useWatch([API, 'educationLevel'], form);
  // Serbian companies pick the national degree (I–VIII). A value entered
  // before that stays listed until someone picks a degree instead.
  const educationLevelOptions = (() => {
    if (market !== 'RS') return EDUCATION_LEVEL_OPTIONS;
    const legacy = EDUCATION_LEVEL_OPTIONS.find(
      (option) => option.value === educationLevel
    );
    return legacy
      ? [...RS_EDUCATION_LEVEL_OPTIONS, legacy]
      : RS_EDUCATION_LEVEL_OPTIONS;
  })().map(({ key, value }) => ({
    value,
    label: t(`educationLevels.${key}`, { ns: 'forms' }),
  }));

  // Children are kept as the old form kept them: the whole list is written
  // back on add / remove, the rows follow the form values.
  const watchedChildren = Form.useWatch([API, 'children'], {
    form,
    preserve: true,
  });
  const [children, setChildren] = useState([]);
  useEffect(() => setChildren(watchedChildren || []), [watchedChildren]);
  const writeChildren = (list) => {
    const current = form.getFieldValue(API) || {};
    form.setFieldsValue({ [API]: { ...current, children: list } });
    setChildren(list);
  };
  const addChild = () =>
    writeChildren([...children, { name: '', birthDate: null }]);
  const removeChild = (index) =>
    writeChildren(children.filter((_, i) => i !== index));

  return (
    <div className={cn('grid gap-4', sectionClass('licno'))}>
      <Block title={tp('groupIdentity')}>
        <FieldGrid>
          {gdText('firstName', f('firstName'), [
            { required: true, message: fv('firstNameRequired') },
            { type: 'firstName', message: fv('firstNameInvalid') },
          ])}
          {gdText('lastName', f('lastName'), [
            { required: true, message: fv('lastNameRequired') },
            { type: 'lastName', message: fv('lastNameInvalid') },
          ])}
          {gdText('personalNumber', f('personalNumber'), [
            { required: true, message: fv('personalNumberRequired') },
          ])}
          <Form.Item name={[API, 'customId']} label={fv('customId')}>
            <Input placeholder={fv('customId')} />
          </Form.Item>
          <Form.Item name={[API, 'birthDate']} label={fv('birthDate')}>
            {datePicker(fv('birthDate'), {
              defaultPickerValue: dayjs('1980-01-01'),
            })}
          </Form.Item>
          <Form.Item name={[API, 'placeOfBirth']} label={fv('placeOfBirth')}>
            <Input placeholder={fv('placeOfBirth')} />
          </Form.Item>
          <Form.Item name={[API, 'gender']} label={fv('gender')}>
            <Select
              allowClear
              placeholder={fv('gender')}
              options={GENDERS.map((key) => ({ value: key, label: fv(key) }))}
            />
          </Form.Item>
          <Form.Item
            name={[API, 'familyCelebrationDate']}
            label={fv('familyCelebrationDate')}
          >
            <DatePicker
              className="w-full"
              format="DD.MM"
              picker="date"
              placeholder={fv('familyCelebrationDate')}
            />
          </Form.Item>
        </FieldGrid>
      </Block>

      <Block title={tp('groupContact')}>
        <FieldGrid>
          {gdText('email', f('email'), [
            { required: false, message: fv('emailRequired') },
            { type: 'email', message: fv('emailInvalid') },
          ])}
          {gdField('phone', f('phoneNumber'), undefined, (disabled) => (
            <Input
              disabled={disabled}
              type="number"
              placeholder={f('phoneNumber')}
              className="[-moz-appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
            />
          ))}
          {gdText('address', f('address'))}
          {gdText('city', f('city'))}
          {gdText('municipality', f('municipality'))}
          {gdText('country', f('country'))}
        </FieldGrid>
      </Block>

      <Block title={tp('groupFamily')}>
        <FieldGrid>
          <Form.Item name={[API, 'maritalStatus']} label={fv('maritalStatus')}>
            <Select
              allowClear
              placeholder={fv('maritalStatus')}
              options={MARITAL_STATUSES.map((key) => ({
                value: key,
                label: fv(key),
              }))}
            />
          </Form.Item>
          <Form.Item name={[API, 'educationLevel']} label={f('educationLevel')}>
            <Select
              allowClear
              placeholder={f('educationLevel')}
              options={educationLevelOptions}
            />
          </Form.Item>
          {educationLevel === 'other' && (
            <Form.Item
              name={[API, 'educationLevelOther']}
              label={f('educationLevelOther')}
              tooltip={f('educationLevelOtherHint')}
              rules={[
                {
                  required: true,
                  whitespace: true,
                  message: f('educationLevelOtherRequired'),
                },
              ]}
            >
              <Input placeholder={f('educationLevelOtherPlaceholder')} />
            </Form.Item>
          )}
        </FieldGrid>

        <div className="mb-2.5 border-t border-zinc-100 pt-2">
          <div className="mb-1.5 flex items-center justify-between">
            <span className="text-[13px] text-zinc-500">{fv('children')}</span>
            {!readOnly && (
              <Button
                type="dashed"
                size="small"
                icon={<PlusOutlined />}
                onClick={addChild}
              >
                {fv('add')}
              </Button>
            )}
          </div>
          {!children.length && (
            <div className="text-[13px] italic text-zinc-300">
              {tp('noChildren')}
            </div>
          )}
          <div className="grid gap-1.5">
            {children.map((_, index) => (
              <div
                key={index}
                className="profile-child-row grid grid-cols-[1fr_150px_auto] items-center gap-2"
              >
                <Form.Item name={[API, 'children', index, 'name']} noStyle>
                  <Input size="small" placeholder={fv('childName')} />
                </Form.Item>
                <Form.Item name={[API, 'children', index, 'birthDate']} noStyle>
                  <DatePicker
                    size="small"
                    className="w-full"
                    format={DATE_FORMATS.DISPLAY}
                    placeholder={fv('birthDate')}
                  />
                </Form.Item>
                {readOnly ? (
                  <span />
                ) : (
                  <Button
                    type="text"
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={() => removeChild(index)}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      </Block>
    </div>
  );
}
